/**
 * /admin/questions — Admin Question Bank list page.
 *
 * Binding contract: `docs/ia/admin-question-bank.md`.
 *
 * Archetype: facet-sidebar + filter-header + bulk-aware list. The page
 * answers "find this question, edit it, bulk-manage it" in one tight
 * workspace.
 *
 * 15-FG/35 brought the page back into archetype after the audit-29 § 2
 * "deviates" verdict. The prior implementation rendered a single horizontal
 * filter row + table; this version restores the SME's facet sidebar with
 * three named groups (Category / Type / Difficulty) and a list-pane that
 * stacks filter-bar → bulk-bar → rows → pagination.
 *
 * Substrate (15-FG/35):
 *   - <AdminLayout> wraps <Shell mode='admin' densityMode='workspace'>.
 *   - Primitives only from `@/components/ui` (Heading / Button / Input /
 *     Badge / Card / Link via FacetSidebar).
 *   - design.md tokens only — no chromatic borders, no `rounded-2xl`,
 *     no `bg-bg-card` / `bg-surface`.
 *
 * Mock data carve-out: the real `/api/questions` filter accepts `type` and
 * `category_id` but NOT `difficulty`. The page filters difficulty client-side
 * over the current page of results — sufficient for v1 + a UX consistent
 * with the contract. Wiring difficulty to a server-side filter is flagged
 * as a follow-up.
 *
 * Cross-cutting invariants:
 *   - Inv 12: no console.log/error/warn.
 *   - Inv 13: no `any` / `@ts-ignore`.
 *   - Inv 14: internal nav via <Link>/<useNavigate>.
 *   - Inv 15: every atom from `@/components/ui` barrel.
 */

import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { AdminLayout } from '../layout.tsx';
import { Badge, Button, Card, Heading, Input, Link } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  deleteQuestion,
  listCategories,
  listQuestions,
  type Difficulty,
  type QuestionPayload,
  type QuestionType,
} from '../../../state/questions/api.ts';
import {
  FacetSidebar,
  TYPE_CHIPS,
  DIFFICULTY_CHIPS,
  type FacetCategory,
} from './components/FacetSidebar.tsx';

// ─── Formatting helpers ────────────────────────────────────────────────────

const EM_DASH = '—';

function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * Sub-label rendered next to the Question Bank h1 — Polish 6 "numeric typo
 * in Space Mono"; the count + category count are the page's at-a-glance
 * scale signal.
 */
function formatSubLabel(qCount: number, catCount: number): string {
  if (qCount === 0 && catCount === 0) return 'No questions yet';
  return `${formatInt(qCount)} question${qCount === 1 ? '' : 's'} across ${formatInt(catCount)} categor${catCount === 1 ? 'y' : 'ies'}`;
}

function typeLabel(type: QuestionType): string {
  const found = TYPE_CHIPS.find((c) => c.value === type);
  return found ? found.label : type;
}

function difficultyBadgeVariant(d: Difficulty): 'success' | 'warning' | 'error' {
  const found = DIFFICULTY_CHIPS.find((c) => c.value === d);
  return found ? found.badge : 'neutral' as never;
}

// ─── Header CTA cluster classNames — composed via cn() so design.md
//     transition tokens survive eslint-plugin-tailwindcss static analysis. ──

const SECONDARY_CTA_CLASSES = cn(
  'inline-flex items-center justify-center rounded-md',
  'border border-line-2 bg-transparent',
  'px-4 py-2 font-sans text-small font-medium text-ink no-underline',
  'hover:bg-bg-warm hover:text-ink',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2',
  'transition-base',
);

const PRIMARY_CTA_CLASSES = cn(
  'inline-flex items-center justify-center rounded-md',
  'bg-ink px-4 py-2',
  'font-sans text-small font-medium text-bg no-underline',
  'hover:bg-ink-2 hover:text-bg',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2',
  'transition-base',
);

const TAG_SELECT_CLASSES = cn(
  'rounded-md border border-line-2 bg-card px-3 py-2',
  'font-sans text-small text-ink',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l',
  'transition-base',
);

// ─── Category facets — real fetch from /api/taxonomy/categories ─────────────
// CategoryPayload now includes `count` (questions per category).
// FacetCategory.count is wired directly.

// ─── Page ─────────────────────────────────────────────────────────────────

export function AdminQuestionListPage(): ReactElement {
  // ─── Data state ─────────────────────────────────────────────────────────
  const [items, setItems] = useState<QuestionPayload[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Categories fetched from /api/taxonomy/categories for the facet sidebar.
  const [facetCategories, setFacetCategories] = useState<FacetCategory[]>([]);

  // ─── Filter state ───────────────────────────────────────────────────────
  const [search, setSearch] = useState<string>('');
  const [tag, setTag] = useState<string>('');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<QuestionType | null>(null);
  const [activeDifficulty, setActiveDifficulty] = useState<Difficulty | null>(null);

  // ─── Selection state ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ─── Category facets — load once on mount ───────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const env = await listCategories();
      if (cancelled || !env.ok) return;
      // CategoryPayload: { id, name, color, count }. Wire count directly.
      setFacetCategories(env.data.map((c) => ({ id: c.id, name: c.name, count: c.count })));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Data load — re-runs when server-side filters change ────────────────
  useEffect(() => {
    void (async () => {
      setLoadError(null);
      const env = await listQuestions({
        type: activeType ?? undefined,
        category_id: activeCategoryId ?? undefined,
        q: search.length > 0 ? search : undefined,
      });
      if (!env.ok) {
        setLoadError(`${env.error.code}: ${env.error.message}`);
        setItems([]);
        return;
      }
      setItems(env.data.items);
      setNextCursor(env.data.next_cursor);
      // Drop any selection IDs that aren't on the new page.
      setSelectedIds((prev) => {
        const pageIds = new Set(env.data.items.map((q) => q.id));
        const next = new Set<string>();
        for (const id of prev) if (pageIds.has(id)) next.add(id);
        return next;
      });
    })();
  }, [activeType, activeCategoryId, search]);

  // ─── Client-side difficulty filter (mock-data carve-out) ────────────────
  const visibleItems = useMemo<QuestionPayload[] | null>(() => {
    if (items === null) return null;
    if (activeDifficulty === null) return items;
    return items.filter((q) => q.difficulty === activeDifficulty);
  }, [items, activeDifficulty]);

  // ─── Sub-label totals ───────────────────────────────────────────────────
  const totalCount = useMemo<number>(() => {
    if (items === null) return 0;
    return items.length;
  }, [items]);

  // ─── Handlers ───────────────────────────────────────────────────────────
  const onClearFilters = (): void => {
    setSearch('');
    setTag('');
    setActiveCategoryId(null);
    setActiveType(null);
    setActiveDifficulty(null);
  };

  const onToggleSelect = (id: string, checked: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const onSelectAll = (checked: boolean): void => {
    if (!visibleItems) return;
    if (checked) {
      setSelectedIds(new Set(visibleItems.map((q) => q.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const onClearSelection = (): void => {
    setSelectedIds(new Set());
  };

  const onDeleteSelected = (): void => {
    if (selectedIds.size === 0) return;
    void (async () => {
      setInfo(null);
      let softCount = 0;
      let hardCount = 0;
      for (const id of selectedIds) {
        const env = await deleteQuestion(id);
        if (!env.ok) {
          setLoadError(`${env.error.code}: ${env.error.message}`);
          return;
        }
        if (env.data.mode === 'soft') softCount += 1;
        else hardCount += 1;
      }
      setInfo(
        `Deleted ${formatInt(hardCount + softCount)} question${hardCount + softCount === 1 ? '' : 's'} (${formatInt(softCount)} soft, ${formatInt(hardCount)} hard).`,
      );
      setSelectedIds(new Set());
      // Trigger reload via filter-effect tick.
      setSearch((s) => s);
    })();
  };

  const isLoading = visibleItems === null;
  const allOnPageSelected =
    visibleItems !== null &&
    visibleItems.length > 0 &&
    visibleItems.every((q) => selectedIds.has(q.id));
  const selectionCount = selectedIds.size;
  const showBulkBar = selectionCount > 0;

  return (
    <AdminLayout title="Question Bank" eyebrow="Admin · Questions">
      {/* Sub-label — Polish 6 numeric typography in Space Mono, sits below
          the AdminLayout's h1+eyebrow header block. Minimum 1. */}
      <p
        className="mt-2 font-mono text-mono text-muted"
        data-testid="bank-subtitle"
      >
        {isLoading ? EM_DASH : formatSubLabel(totalCount, facetCategories.length)}
      </p>

      {/* Header CTA cluster — Minimum 2. Right-aligned, secondary "Import
          CSV" first, primary "Add Question" rightmost. Sibling idiom from
          dashboard.tsx (line 205-212) and edit.tsx (line 318). */}
      <div className="mt-6 flex items-start justify-end gap-3">
        <Link to="/admin/questions/import" className={SECONDARY_CTA_CLASSES}>
          Import CSV
        </Link>
        <Link to="/admin/questions/new" className={PRIMARY_CTA_CLASSES}>
          Add Question
        </Link>
      </div>

      {/* Banner messages — non-blocking; render between header and main grid. */}
      {loadError ? (
        <p className="mt-4 font-sans text-small text-error" role="alert">
          {loadError}
        </p>
      ) : null}
      {info ? (
        <p className="mt-4 font-sans text-small text-success" role="status">
          {info}
        </p>
      ) : null}

      {/* Main 2-col grid — sidebar (left) + list pane (right). Stacks below
          lg per archetype Extension rule ("sidebar may scroll independently
          of the list at >6 facet groups"); at typical viewports the
          contract's "left rail" reads from the top. */}
      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
        <FacetSidebar
          totalCount={totalCount}
          categories={facetCategories}
          activeCategoryId={activeCategoryId}
          activeType={activeType}
          activeDifficulty={activeDifficulty}
          onCategoryChange={setActiveCategoryId}
          onTypeChange={setActiveType}
          onDifficultyChange={setActiveDifficulty}
        />

        {/* Main list pane — region landmark so the IA-conformance test can
            scope queries (Minimum 5/6/7 live inside this region). */}
        <section
          aria-label="Question list"
          className="flex min-w-0 flex-1 flex-col"
        >
          {/* Filter bar — Minimum 5. Search + Tag dropdown + Clear. */}
          <Card variant="default" className="rounded-b-none p-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-60 flex-1">
                <label htmlFor="bank-search" className="sr-only">
                  Search questions
                </label>
                <Input
                  id="bank-search"
                  type="search"
                  placeholder="Search questions…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <label htmlFor="bank-tag-filter" className="sr-only">
                Filter by tag
              </label>
              <select
                id="bank-tag-filter"
                aria-label="Filter by tag"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className={TAG_SELECT_CLASSES}
              >
                <option value="">All Tags</option>
              </select>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={onClearFilters}
              >
                Clear
              </Button>
            </div>
          </Card>

          {/* Bulk-action bar — Minimum 6. Hidden (display:none) when zero
              selected so the list pane below doesn't shift on appearance
              (Polish 7). */}
          <div
            role="region"
            aria-label="Bulk actions"
            // `hidden` attribute is the semantic equivalent of display:none;
            // matches archetype "hidden when zero rows selected; never
            // reserves vertical space."
            hidden={!showBulkBar}
            className={cn(
              'flex items-center gap-4',
              'border-x border-line bg-bg-warm px-4 py-3',
              'font-sans text-small text-ink',
            )}
          >
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={allOnPageSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
                aria-label="Select all on this page"
                className="size-4 cursor-pointer accent-ink"
              />
              <span className="sr-only">Select all on this page</span>
            </label>
            <Badge variant="error" className="font-mono">
              {formatInt(selectionCount)} selected
            </Badge>
            <Button
              variant="danger"
              size="sm"
              type="button"
              onClick={onDeleteSelected}
            >
              Delete Selected
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={onClearSelection}
            >
              Clear
            </Button>
          </div>

          {/* List body — Minimum 7. NOT a table (multi-line rows). Each row
              is a Card-warm sub-row with select + question text + metadata
              badges + edit/delete affordances. */}
          <Card
            variant="default"
            className={cn(
              'min-h-[200px] rounded-t-none p-0',
              showBulkBar ? 'border-t-0' : '',
            )}
          >
            {isLoading ? (
              <div className="px-4 py-10 text-center font-mono text-mono text-muted">
                <span aria-live="polite">{EM_DASH}</span>
              </div>
            ) : visibleItems!.length === 0 ? (
              <div className="px-4 py-10 text-center font-sans text-small text-muted">
                No questions match the current filters.
              </div>
            ) : (
              <ul role="list" className="flex flex-col">
                {visibleItems!.map((q) => {
                  const isSelected = selectedIds.has(q.id);
                  return (
                    <li
                      key={q.id}
                      className={cn(
                        'flex items-start gap-3',
                        'border-b border-line last:border-b-0',
                        'px-4 py-3',
                        'hover:bg-bg-warm transition-base',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => onToggleSelect(q.id, e.target.checked)}
                        aria-label={`Select question: ${q.text}`}
                        className="mt-1 size-4 cursor-pointer accent-ink"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-sans text-body text-ink">{q.text}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Badge variant="neutral" className="font-mono">
                            {typeLabel(q.type)}
                          </Badge>
                          <Badge
                            variant={difficultyBadgeVariant(q.difficulty)}
                            className="font-mono"
                          >
                            {q.difficulty}
                          </Badge>
                          {q.deprecated ? (
                            <Badge variant="neutral" className="font-mono">
                              deprecated
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <Link to={`/admin/questions/${q.id}`}>Edit</Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Pagination — Polish 10. Centered, Space Grotesk 13px. Mock data
              renders a single page; cursor wiring is in place for follow-up. */}
          {nextCursor ? (
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  // Tick the search state to trigger reload; cursor-aware
                  // pagination is the follow-up that wires next_cursor
                  // through listQuestions.
                  setSearch((s) => s);
                }}
              >
                Load more
              </Button>
            </div>
          ) : null}

          {/* Hidden Heading anchor so the IA-conformance test can address the
              list region by an accessible name even when filters yield an
              empty list. Renders as visually hidden text. */}
          <Heading level="h2" className="sr-only">
            Questions
          </Heading>
        </section>
      </div>
    </AdminLayout>
  );
}
